/**
 * useUsageRecordByRequest — Doc 25 §F2.6.
 *
 * Consulta `/usage/records?request_id=X` para obtener los tokens reales
 * de una invocación específica del comment review agent (o de cualquier
 * agente que haya pasado `request_id`).
 *
 * Antes (pre-F2.5) los campos `tokens_input/output/latency_ms/model_id`
 * venían dentro del response del endpoint `/comments/.../ai-suggest`.
 * F2.5 dropeó esas columnas de la BD; ahora la fuente única de verdad
 * es `llm_usage_records`. Este hook centraliza el fetch + agregación:
 * suma `input_tokens` + `output_tokens` + `latency_ms` y devuelve un
 * shape mínimo cómodo para el modal del IA co-reviewer.
 *
 * Cache 60s — los records son append-only, no cambian post-creación.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import {
  type UsageRecord,
  getUsageRecords,
} from '../api/usage'
import { useAccessToken } from '../auth/useAccessToken'

export interface UsageByRequestSummary {
  tokens_input: number
  tokens_output: number
  latency_ms: number
  /** Modelo "principal" (el del long_form draft, no la síntesis fast). */
  model_id: string
  /** Cantidad de calls al LLM bajo este request_id (síntesis + agent). */
  call_count: number
}

function _aggregate(records: UsageRecord[]): UsageByRequestSummary {
  let tokens_input = 0
  let tokens_output = 0
  let latency_ms = 0
  let main_model = ''
  let main_output = 0
  for (const r of records) {
    tokens_input += r.input_tokens
    tokens_output += r.output_tokens
    if (r.latency_ms !== null) latency_ms += r.latency_ms
    // El modelo "principal" es el del step con más output_tokens —
    // típicamente el long_form draft. La síntesis fast tiene mucho
    // menos output, así que pierde el max.
    if (r.output_tokens > main_output) {
      main_output = r.output_tokens
      main_model = r.model_id
    }
  }
  return {
    tokens_input,
    tokens_output,
    latency_ms,
    model_id: main_model,
    call_count: records.length,
  }
}

export function useUsageRecordByRequest(args: {
  requestId: string | null | undefined
  enabled?: boolean
  activeTenantId?: string
}): UseQueryResult<UsageByRequestSummary | null, Error> {
  const { accessToken } = useAccessToken()
  const enabled =
    (args.enabled ?? true) &&
    !!accessToken &&
    !!args.requestId

  return useQuery({
    queryKey: ['usage-records-by-request', args.requestId],
    queryFn: async () => {
      if (!accessToken || !args.requestId) {
        return null
      }
      const page = await getUsageRecords(
        accessToken,
        { requestId: args.requestId, limit: 50 },
        args.activeTenantId
          ? { activeTenantId: args.activeTenantId }
          : {},
      )
      return _aggregate(page.items)
    },
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  })
}
